import {
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ChevronRight,
  Inbox,
  Lock,
  Mail,
  Plus,
  Shield,
  Sparkles,
  TrendingUp,
  User,
  X,
} from "lucide-react";

import {
  Avatar,
  Badge,
  Button,
  Card,
  Field,
  Input,
  Separator,
  Spinner,
  ToggleGroup,
} from "@/components/ui";
import { cn } from "@/utils/Functions";

import {
  CONNECT_SOURCES,
  INDUSTRIES,
  ROLE_DEFS,
  ROLES,
  SAMPLE_INCOMING_DEALS,
  TEAM_SIZES,
} from "./constants";
import { AriBrandOrFallback } from "@/components/demo-ari/AriBrandIcon";
import { resolveAriBrandId } from "@/lib/ariBrands";
import { ConnectBrandIcon, ConnectIconWrapper } from "./components/ConnectBrandIcons";
import type { DemoWelcomeState, InviteRoleId } from "./types";
import { WelcomeActionRow } from "./components/WelcomeActionRow";
import { WelcomeStepTitle } from "./components/WelcomeStepTitle";

const stepShell = "flex min-h-0 flex-1 flex-col overflow-auto px-11 py-9 max-lg:px-6";

type StepProps = {
  state: DemoWelcomeState;
  setState: Dispatch<SetStateAction<DemoWelcomeState>>;
  next: () => void;
  back?: () => void;
};

function ChoiceChip({
  selected,
  onClick,
  children,
  className,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Button
      type="button"
      variant={selected ? "default" : "outline"}
      size="sm"
      onClick={onClick}
      className={cn("h-auto rounded-md px-3 py-1.5 text-xs font-normal", className)}
    >
      {children}
    </Button>
  );
}

export function StepSignIn({ state, setState, next }: StepProps) {
  const valid = /^\S+@\S+\.\S+$/.test(state.email);

  return (
    <div className="min-h-0 flex-1 flex-col overflow-auto px-11 py-9 max-lg:px-6 grid place-items-center">
      <div>
      <WelcomeStepTitle
        eyebrow="Welcome to Dealflow"
        title="Sign in or create an account"
        sub="Use your work email, the address where your deal emails come in."
      />
        

      <div className="flex flex-col gap-3.5">
        <Field>
          <Field.Label>Work email</Field.Label>
          <Input
            type="email"
            autoFocus
            placeholder="you@company.com"
            value={state.email}
            onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && valid) next();
            }}
          />
        </Field>

        <Button type="button" disabled={!valid} onClick={next} className="w-full">
          Continue
          <ArrowRight className="size-4" />
        </Button>

        <div className="my-1 flex items-center gap-2.5 text-[11px] text-muted-foreground">
          <Separator className="flex-1" />
          <span>or</span>
          <Separator className="flex-1" />
        </div>

        {[
          {
            label: "Continue with Google",
            sub: "Recommended. Connects Gmail in one step",
          },
          { label: "Continue with Apple" },
          { label: "Single Sign-On (SAML/SSO)", sub: "For enterprise teams" },
        ].map((provider) => (
          <Button
            key={provider.label}
            type="button"
            variant="outline"
            className="h-auto w-full justify-between px-3.5 py-2.5 text-left"
            onClick={next}
          >
            <span>
              <span className="block text-sm font-medium">{provider.label}</span>
              {provider.sub ? (
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  {provider.sub}
                </span>
              ) : null}
            </span>
            <ChevronRight className="size-3.5 text-muted-foreground" />
          </Button>
        ))}
      </div>

      <p className="mt-auto flex items-center gap-1.5 pt-6 text-[11px] text-muted-foreground">
        <Lock className="size-3 shrink-0" aria-hidden />
        SOC 2 Type II · Your data never leaves your accounts.
      </p>
    </div>
    </div>
  );
}

export function StepAbout({ state, setState, next, back }: StepProps) {
  const valid = state.name.trim().length >= 2 && state.role && state.industry;

  return (
    <div className={stepShell}>
      <WelcomeStepTitle
        eyebrow="Step 2 · About you"
        title="Who are we setting up?"
        sub="Ari uses this to recommend templates and surface signals relevant to your work."
      />

      <div className="flex flex-col gap-4.5">
        <Field>
          <Field.Label>Your name</Field.Label>
          <Input
            autoFocus
            placeholder="Alex Morrison"
            value={state.name}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
          />
        </Field>

        <Field>
          <Field.Label>Your role</Field.Label>
          <div className="flex flex-wrap gap-1.5">
            {ROLES.map((role) => (
              <ChoiceChip
                key={role}
                selected={state.role === role}
                onClick={() => setState((s) => ({ ...s, role }))}
              >
                {role}
              </ChoiceChip>
            ))}
          </div>
        </Field>

        <Field>
          <Field.Label>Industry</Field.Label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {INDUSTRIES.map((industry) => (
              <button
                key={industry.id}
                type="button"
                onClick={() => setState((s) => ({ ...s, industry: industry.id }))}
                className={cn(
                  "rounded-lg border px-3 py-2.5 text-left transition-colors",
                  state.industry === industry.id
                    ? "border-primary bg-primary/10"
                    : "border-border bg-muted/30 hover:bg-muted/50",
                )}
              >
                <div
                  className={cn(
                    "text-sm font-semibold",
                    state.industry === industry.id ? "text-primary" : "text-foreground",
                  )}
                >
                  {industry.label}
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {industry.desc}
                </div>
              </button>
            ))}
          </div>
        </Field>
      </div>

      <WelcomeActionRow>
        <Button type="button" variant="outline" onClick={back}>
          <ArrowLeft className="size-3.5" />
          Back
        </Button>
        <div className="flex-1" />
        <Button type="button" disabled={!valid} onClick={next}>
          Continue
          <ArrowRight className="size-4" />
        </Button>
      </WelcomeActionRow>
    </div>
  );
}

export function StepWorkspace({ state, setState, next, back }: StepProps) {
  const slug =
    (state.orgName || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "your-org";

  const valid =
    state.mode === "join"
      ? state.joinCode.trim().length > 2
      : state.orgName.trim().length >= 2 && Boolean(state.size);

  return (
    <div className={stepShell}>
      <WelcomeStepTitle
        eyebrow="Step 3 · Workspace"
        title="Create your organization"
        sub="A workspace holds all your deals, contracts, and team members. You can rename it later."
      />

      <ToggleGroup
        value={[state.mode]}
        onValueChange={(value) => {
          const nextMode = value[0];
          if (nextMode === "create" || nextMode === "join") {
            setState((s) => ({ ...s, mode: nextMode }));
          }
        }}
        className="mb-4.5 w-fit rounded-lg bg-muted p-0.5"
      >
        <ToggleGroup.Item value="create" className="rounded-md px-3.5 py-1.5 text-xs">
          Create new
        </ToggleGroup.Item>
        <ToggleGroup.Item value="join" className="rounded-md px-3.5 py-1.5 text-xs">
          Join existing
        </ToggleGroup.Item>
      </ToggleGroup>

      {state.mode === "create" ? (
        <div className="flex flex-col gap-4.5">
          <Field>
            <Field.Label>Organization name</Field.Label>
            <Input
              autoFocus
              placeholder="Morrison Talent Group"
              value={state.orgName}
              onChange={(e) => setState((s) => ({ ...s, orgName: e.target.value }))}
            />
          </Field>

          <Field>
            <Field.Label>Workspace URL</Field.Label>
            <Field.Description>Your team will use this to sign in.</Field.Description>
            <div className="flex items-center gap-0 rounded-lg border border-input bg-muted/30 px-3 py-2.5">
              <span className="font-mono text-xs text-muted-foreground">equaliq.com/</span>
              <span className="font-mono text-xs font-medium text-primary">{slug}</span>
              <Check className="ml-auto size-3.5 text-primary" aria-hidden />
            </div>
          </Field>

          <Field>
            <Field.Label>Team size</Field.Label>
            <div className="flex flex-wrap gap-1.5">
              {TEAM_SIZES.map((size) => (
                <ChoiceChip
                  key={size}
                  selected={state.size === size}
                  onClick={() => setState((s) => ({ ...s, size }))}
                >
                  {size}
                </ChoiceChip>
              ))}
            </div>
          </Field>
        </div>
      ) : (
        <div className="flex flex-col gap-3.5">
          <Field>
            <Field.Label>Invite code or workspace URL</Field.Label>
            <Field.Description>
              Ask your admin for the code if you don&apos;t have one.
            </Field.Description>
            <Input
              autoFocus
              placeholder="e.g. ROC-NATION or equaliq.com/roc-nation"
              value={state.joinCode}
              onChange={(e) => setState((s) => ({ ...s, joinCode: e.target.value }))}
            />
          </Field>
          <div className="rounded-lg border border-border bg-muted/30 px-3.5 py-3 text-xs text-muted-foreground">
            <p className="mb-1 flex items-center gap-1.5 font-medium text-foreground">
              <Shield className="size-3.5 text-primary" aria-hidden />
              You&apos;ll join with the role assigned by your admin
            </p>
            The owner has already configured what you can see and do.
          </div>
        </div>
      )}

      <WelcomeActionRow>
        <Button type="button" variant="outline" onClick={back}>
          <ArrowLeft className="size-3.5" />
          Back
        </Button>
        <div className="flex-1" />
        <Button type="button" disabled={!valid} onClick={next}>
          Continue
          <ArrowRight className="size-4" />
        </Button>
      </WelcomeActionRow>
    </div>
  );
}

export function StepInvite({
  state,
  setState,
  next,
  back,
  isOwner,
}: StepProps & { isOwner: boolean }) {
  const [draftEmail, setDraftEmail] = useState("");
  const [draftRole, setDraftRole] = useState<InviteRoleId>("negotiator");

  const addInvite = () => {
    if (!/^\S+@\S+\.\S+$/.test(draftEmail)) return;
    if (state.invites.some((i) => i.email === draftEmail)) return;
    setState((s) => ({
      ...s,
      invites: [...s.invites, { email: draftEmail, role: draftRole }],
    }));
    setDraftEmail("");
  };

  const removeInvite = (email: string) => {
    setState((s) => ({ ...s, invites: s.invites.filter((i) => i.email !== email) }));
  };

  const changeRole = (email: string, role: InviteRoleId) => {
    setState((s) => ({
      ...s,
      invites: s.invites.map((i) => (i.email === email ? { ...i, role } : i)),
    }));
  };

  if (!isOwner) {
    const role = ROLE_DEFS[2];
    return (
      <div className={stepShell}>
        <WelcomeStepTitle
          eyebrow="Step 4 · Team"
          title="You're joining an existing team"
          sub="Your admin already configured the roster. You can request access to additional deals later."
        />
        <div className="mb-4 rounded-lg border border-border bg-muted/30 p-4">
          <p className="mb-2.5 text-[11px] font-semibold text-muted-foreground">
            Your assigned role
          </p>
          <div className="flex items-center gap-2.5">
            <div className="flex size-6 items-center justify-center rounded-md bg-primary/10 text-primary">
              <User className="size-3.5" aria-hidden />
            </div>
            <div>
              <p className="text-sm font-semibold">{role.label}</p>
              <p className="text-[11px] text-muted-foreground">{role.desc}</p>
            </div>
          </div>
        </div>
        <WelcomeActionRow>
          <Button type="button" variant="outline" onClick={back}>
            <ArrowLeft className="size-3.5" />
            Back
          </Button>
          <div className="flex-1" />
          <Button type="button" onClick={next}>
            Continue
            <ArrowRight className="size-4" />
          </Button>
        </WelcomeActionRow>
      </div>
    );
  }

  return (
    <div className={stepShell}>
      <WelcomeStepTitle
        eyebrow="Step 4 · Invite team"
        title="Add your team and assign roles"
        sub="As the owner, you control who sees what. Roles can be changed anytime from settings."
      />

      <div className="mb-3.5 grid grid-cols-2 gap-1.5 lg:grid-cols-5">
        {ROLE_DEFS.map((role) => (
          <div
            key={role.id}
            className="flex flex-col gap-1 rounded-md border border-border bg-muted/20 px-2 py-2"
          >
            <div className="flex items-center gap-1.5">
              <span className={cn("size-1.5 rounded-full", role.dotClassName)} aria-hidden />
              <span className="text-[11px] font-semibold">{role.label}</span>
            </div>
            <span className="text-[10px] leading-snug text-muted-foreground">{role.desc}</span>
          </div>
        ))}
      </div>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        <Input
          className="flex-1"
          placeholder="teammate@company.com"
          value={draftEmail}
          onChange={(e) => setDraftEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") addInvite();
          }}
        />
        <select
          value={draftRole}
          onChange={(e) => setDraftRole(e.target.value as InviteRoleId)}
          className="rounded-lg border border-input bg-background px-3 py-2 text-xs"
          aria-label="Invite role"
        >
          {ROLE_DEFS.filter((r) => r.id !== "owner").map((role) => (
            <option key={role.id} value={role.id}>
              {role.label}
            </option>
          ))}
        </select>
        <Button type="button" variant="outline" onClick={addInvite}>
          <Plus className="size-3.5" />
          Add
        </Button>
      </div>

      <div className="mb-3.5 flex min-h-0 flex-1 flex-col gap-1.5 overflow-auto">
        <div className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5">
          <Avatar
            className="size-7 rounded-md"
            fallback={(state.name || state.email || "You").slice(0, 2).toUpperCase()}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium">
              {state.name || "You"}{" "}
              <span className="font-normal text-muted-foreground">· {state.email}</span>
            </p>
            <p className="text-[10.5px] text-muted-foreground">That&apos;s you · sign-up</p>
          </div>
          <Badge variant="default" className="text-[10px]">
            Owner
          </Badge>
        </div>

        {state.invites.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-3.5 py-4 text-center text-xs text-muted-foreground">
            No invites yet. Add teammates above or skip and invite later.
          </div>
        ) : (
          state.invites.map((invite) => {
            const role = ROLE_DEFS.find((r) => r.id === invite.role) ?? ROLE_DEFS[2];
            return (
              <div
                key={invite.email}
                className="flex items-center gap-2.5 rounded-lg border border-border bg-muted/20 px-3 py-2.5"
              >
                <div className="flex size-7 items-center justify-center rounded-md bg-muted text-[11px] font-semibold text-muted-foreground">
                  {invite.email.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium">{invite.email}</p>
                  <p className="text-[10.5px] text-muted-foreground">Pending invite</p>
                </div>
                <select
                  value={invite.role}
                  onChange={(e) => changeRole(invite.email, e.target.value as InviteRoleId)}
                  className="rounded-md border border-input bg-background px-2 py-1 text-[11px] font-medium text-foreground"
                  aria-label={`Role for ${invite.email}`}
                >
                  {ROLE_DEFS.filter((r) => r.id !== "owner").map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => removeInvite(invite.email)}
                  aria-label={`Remove ${invite.email}`}
                >
                  <X className="size-3.5" />
                </Button>
              </div>
            );
          })
        )}
      </div>

      <WelcomeActionRow>
        <Button type="button" variant="outline" onClick={back}>
          <ArrowLeft className="size-3.5" />
          Back
        </Button>
        <div className="flex-1" />
        <Button type="button" variant="outline" onClick={next}>
          Skip for now
        </Button>
        <Button type="button" onClick={next}>
          {state.invites.length > 0
            ? `Send ${state.invites.length} ${state.invites.length === 1 ? "invite" : "invites"}`
            : "Continue"}
          <ArrowRight className="size-4" />
        </Button>
      </WelcomeActionRow>
    </div>
  );
}

export function StepConnect({ state, setState, next, back }: StepProps) {
  const toggleConnect = (id: string) => {
    if (state.connected.includes(id) || state.connecting) return;
    setState((s) => ({ ...s, connecting: id }));
    window.setTimeout(() => {
      setState((s) => ({
        ...s,
        connecting: null,
        connected: [...s.connected, id],
      }));
    }, 1100);
  };

  const gmailConnected = state.connected.includes("gmail");

  return (
    <div className={stepShell}>
      <WelcomeStepTitle
        eyebrow="Step 5 · Connect"
        title="Connect your stack"
        sub="Gmail is required, that's where deals come in. The rest are optional but unlock more of Ari."
      />

      <div className="mb-4 flex flex-col gap-2">
        {CONNECT_SOURCES.map((source) => {
          const isConnected = state.connected.includes(source.id);
          const isConnecting = state.connecting === source.id;
          return (
            <div
              key={source.id}
              className={cn(
                "flex items-center gap-3 rounded-lg border px-3.5 py-3 transition-colors",
                isConnected
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-muted/20",
              )}
            >
              <ConnectIconWrapper sourceId={source.id} connected={isConnected}>
                <ConnectBrandIcon sourceId={source.id} />
              </ConnectIconWrapper>
              <div className="min-w-0 flex-1">
                <div className="mb-0.5 flex items-center gap-1.5">
                  <span className="text-sm font-semibold">{source.brand}</span>
                  {source.required ? (
                    <Badge
                      variant="secondary"
                      className={cn(
                        "text-sm",
                        isConnected && "border-background/30 bg-background text-foreground",
                      )}
                    >
                      Required
                    </Badge>
                  ) : null}
                </div>
                <p
                  className={cn(
                    "text-sm",
                    isConnected ? "text-background/90" : "text-muted-foreground",
                  )}
                >
                  {source.sub} · {source.detail}
                </p>
              </div>
              {isConnected ? (
                <Badge
                  className="gap-1 border-background/30 bg-background text-foreground text-[11.5px]"
                >
                  <Check className="size-3 text-success" aria-hidden />
                  Connected
                </Badge>
              ) : isConnecting ? (
                <span className="flex items-center gap-1.5 px-2 text-[11.5px] font-medium text-primary">
                  <Spinner className="size-3" />
                  Connecting…
                </span>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => toggleConnect(source.id)}
                >
                  Connect
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <p className="mb-3.5 flex items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2.5 text-[11px] text-muted-foreground">
        <Lock className="size-3 shrink-0" aria-hidden />
        OAuth & token-based · Ari never stores your passwords. Revoke any time from Settings →
        Connections.
      </p>

      <WelcomeActionRow>
        <Button type="button" variant="outline" onClick={back}>
          <ArrowLeft className="size-3.5" />
          Back
        </Button>
        <div className="flex-1" />
        <Button type="button" disabled={!gmailConnected} onClick={next}>
          {gmailConnected ? "Build my workspace" : "Connect Gmail to continue"}
          <ArrowRight className="size-4" />
        </Button>
      </WelcomeActionRow>
    </div>
  );
}

function formatPipelineValue(millions: number) {
  return millions >= 1000 ? `$${(millions / 1000).toFixed(1)}B` : `$${Math.round(millions)}M`;
}

export function StepPopulate({
  state,
  onLaunch,
}: {
  state: DemoWelcomeState;
  onLaunch: () => void;
}) {
  const [tick, setTick] = useState(0);
  const [stage, setStage] = useState<"scanning" | "ingesting" | "done">("scanning");
  const [emails, setEmails] = useState<(typeof SAMPLE_INCOMING_DEALS)[number][]>([]);
  const [scanProgress, setScanProgress] = useState(0);

  const totalThreads = 1842;

  useEffect(() => {
    const timer = window.setInterval(() => setTick((t) => t + 1), 100);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (tick < 9) {
      setStage("scanning");
      setScanProgress(Math.min(1, tick / 8));
      return;
    }
    if (tick === 9) setStage("ingesting");
    if (stage === "ingesting" && tick >= 9) {
      const desired = Math.min(SAMPLE_INCOMING_DEALS.length, Math.floor((tick - 9) / 2));
      if (desired > emails.length) {
        setEmails(SAMPLE_INCOMING_DEALS.slice(0, desired));
      }
      if (desired === SAMPLE_INCOMING_DEALS.length && tick > 9 + SAMPLE_INCOMING_DEALS.length * 2 + 6) {
        setStage("done");
      }
    }
  }, [tick, stage, emails.length]);

  const dealsFound = emails.length;
  const totalValue = emails.reduce((sum, deal) => {
    const match = deal.amount.match(/[\d.]+/);
    if (!match) return sum;
    const n = parseFloat(match[0]);
    return sum + (deal.amount.includes("B") ? n * 1000 : n);
  }, 0);

  const firstName = (state.name || "").split(" ")[0] || "there";

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-9 py-7 max-lg:px-5">
      <div className="mb-4">
        <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-primary">
          <span className="size-1.5 animate-pulse rounded-full bg-primary" aria-hidden />
          {stage === "scanning"
            ? "Scanning your inbox"
            : stage === "ingesting"
              ? "Ingesting deals"
              : "Workspace ready"}
        </p>
        <h1 className="mb-1.5 text-2xl font-semibold">
          {stage === "done" ? `Welcome to Dealflow, ${firstName}` : "Building your workspace…"}
        </h1>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {stage === "scanning" &&
            `Reading ${totalThreads.toLocaleString()} threads across your Gmail history.`}
          {stage === "ingesting" &&
            `Found ${dealsFound} deal threads so far, Ari is attaching contracts and counterparties as they come in.`}
          {stage === "done" &&
            `${dealsFound} active deals across ${new Set(emails.map((e) => e.client)).size} clients ready to go.`}
        </p>
      </div>

      <div className="mb-3.5 grid grid-cols-3 gap-2">
        {[
          {
            label: "Threads scanned",
            value:
              stage === "scanning"
                ? Math.round(totalThreads * scanProgress).toLocaleString()
                : totalThreads.toLocaleString(),
            icon: Mail,
            iconClassName: "text-primary",
          },
          {
            label: "Deals identified",
            value: String(dealsFound),
            icon: TrendingUp,
            iconClassName: "text-success",
          },
          {
            label: "Pipeline value",
            value: formatPipelineValue(totalValue),
            icon: Building2,
            iconClassName: "text-warning",
          },
        ].map((metric) => (
          <Card key={metric.label} className="px-3 py-2.5">
            <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold text-muted-foreground">
              <metric.icon className={cn("size-3", metric.iconClassName)} aria-hidden />
              {metric.label}
            </p>
            <p className="font-mono text-lg font-semibold">{metric.value}</p>
          </Card>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
          <Inbox className="size-3 text-primary" aria-hidden />
          <span className="text-xs font-semibold">Inbox</span>
          <span className="text-[10px] text-muted-foreground">· {emails.length} threads</span>
          <div className="flex-1" />
          {stage === "ingesting" ? (
            <span className="flex items-center gap-1 text-[10.5px] text-primary">
              <span className="size-1.5 animate-pulse rounded-full bg-primary" aria-hidden />
              Live
            </span>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-auto py-1">
          {emails.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">
              {stage === "scanning" ? (
                <>
                  <Spinner className="mx-auto mb-2.5 size-7" />
                  Reading {totalThreads.toLocaleString()} threads…
                </>
              ) : (
                "Waiting for first deal…"
              )}
            </div>
          ) : (
            emails.map((email, index) => (
              <div
                key={`${email.client}-${email.subject}`}
                className="demo-welcome-inbox-row flex items-center gap-2.5 border-b border-border px-3 py-2 last:border-b-0"
                style={{ animationDelay: `${(emails.length - 1 - index) * 20}ms` }}
              >
                <span className="size-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                <AriBrandOrFallback
                  brandId={resolveAriBrandId(email.org, email.subject)}
                  size="sm"
                  className="rounded-md"
                  fallback={
                    <div className="flex size-6.5 shrink-0 items-center justify-center rounded-md bg-muted text-[10px] font-semibold text-foreground">
                      {email.from
                        .split(" ")
                        .map((part) => part[0])
                        .slice(0, 2)
                        .join("")}
                    </div>
                  }
                />
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 flex items-center gap-1.5">
                    <span className="text-xs font-semibold">{email.from}</span>
                    <span className="text-[10px] text-muted-foreground">· {email.org}</span>
                  </div>
                  <p className="truncate text-[11.5px] text-muted-foreground">{email.subject}</p>
                </div>
                <Badge variant="secondary" className="shrink-0 text-[9.5px]">
                  {email.tag}
                </Badge>
                <span className="min-w-12 shrink-0 text-right font-mono text-[11px] font-medium text-muted-foreground">
                  {email.amount}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mt-3.5 flex items-center gap-2.5">
        <p className="flex-1 text-[11.5px] text-muted-foreground">
          {stage === "done" ? (
            <span className="inline-flex items-center gap-1.5">
              <Check className="size-3 text-success" aria-hidden />
              All set. Ari is ready when you are.
            </span>
          ) : (
            "Connecting Stripe & Plaid in background, you can finish setup later."
          )}
        </p>
        <Button type="button" disabled={stage !== "done"} onClick={onLaunch}>
          <Sparkles className="size-3.5" />
          Launch
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}
